import { createBrowserRouter } from "react-router-dom";
import ProtectedRoute from "./protected";
import React from "react";
import { SidebarProvider } from "../context/sidebar";

const Layout = React.lazy(() => import("../layouts"));
const Dashboard = React.lazy(() => import("../pages/dashboard"));
const Forms = React.lazy(() => import("../pages/forms"));
const JobOpeningPage = React.lazy(() => import("../pages/jobOpenings"));
const JobOpeningCreatePage = React.lazy(() => import("../pages/jobOpenings/create"));
const JobOpeningEditPage = React.lazy(() => import("../pages/jobOpenings/edit/page"));
const AnggaranPage = React.lazy(() => import("../pages/anggaran"));
const AnggaranCreatePage = React.lazy(() => import("../pages/anggaran/create/page"));
const AnggaranEditPage = React.lazy(() => import("../pages/anggaran/edit/page"));
const AnggaranAttachmentDownloadPage = React.lazy(() => import("../pages/anggaran/attachmentDownload"));
const FundRequestPage = React.lazy(() => import("../pages/fund-request"));
const FundRequestForm = React.lazy(() => import("../pages/fund-request/form"));
const TransactionsPage = React.lazy(() => import("../pages/transactions"));
const TransactionForm = React.lazy(() => import("../pages/transactions/form"));
const KodeAkunPage = React.lazy(() => import("../pages/configuration/account-codes/page"));
const KodeAkunFormPage = React.lazy(() => import("../pages/configuration/account-codes/form/page"));
const PosAnggaranPage = React.lazy(() => import("../pages/configuration/budget-posts"));
const PosAnggaranFormPage = React.lazy(() => import("../pages/configuration/budget-posts/form/page"));
const WorkflowPage = React.lazy(() => import("../pages/configuration/workflow"));
const WorkflowFormPage = React.lazy(() => import("../pages/configuration/workflow/form/page"));
const PeriodePage = React.lazy(() => import("../pages/configuration/periode"));
const PeriodeFormPage = React.lazy(() => import("../pages/configuration/periode/form/page"));
const BudgetSettingsPage = React.lazy(() => import("../pages/configuration/budget-settings"));
const BudgetSettingsFormPage = React.lazy(() => import("../pages/configuration/budget-settings/form/page"));
const FundRequestSettingsPage = React.lazy(() => import("../pages/configuration/fund-request-settings/page"));
const Login = React.lazy(() => import("../pages/login"));
const Auth = React.lazy(() => import("../pages/auth"));
const NotFound = React.lazy(() => import("../pages/404"));

const MainRouter = createBrowserRouter([
  {
    path: "/",
    element: (
      <ProtectedRoute
        element={
          <SidebarProvider>
            <Layout />
          </SidebarProvider>
        }
      />
    ),
    children: [
      { index: true, element: <Dashboard />, handle: { name: "Beranda" } },
      { path: "forms", element: <Forms />, handle: { name: "Formulir" } },
      {
        path: "lowongan-kerja",
        handle: { name: "Lowongan Kerja" },
        children: [
          {
            index: true,
            element: <JobOpeningPage />,
            handle: { name: "Daftar" },
          },
          {
            path: "buat",
            element: <JobOpeningCreatePage />,
            handle: { name: "Buat" },
          },
          {
            path: "edit/:id",
            element: <JobOpeningEditPage />,
            handle: { name: "Edit" },
          }
        ]
      },
      {
        path: "anggaran",
        handle: { name: "Anggaran" },
        children: [
          {
            index: true,
            element: <AnggaranPage />,
            handle: { name: "Daftar" },
          },
          {
            path: "buat",
            element: <AnggaranCreatePage />,
            handle: { name: "Buat" },
          },
          {
            path: "edit/:id",
            element: <AnggaranEditPage />,
            handle: { name: "Edit" },
          },
          {
            path: ":budgetId/attachments/:detailId/:attachmentId/download",
            element: <AnggaranAttachmentDownloadPage />,
            handle: { name: "Unduh Lampiran" },
          }
        ]
      },
      {
        path: "permohonan-dana",
        handle: { name: "Permohonan Dana" },
        children: [
          { index: true, element: <FundRequestPage />, handle: { name: "Daftar" } },
          { path: "buat", element: <FundRequestForm />, handle: { name: "Buat" } },
          { path: "edit/:id", element: <FundRequestForm />, handle: { name: "Detail" } },
        ],
      },
      {
        path: "transaksi",
        handle: { name: "Transaksi" },
        children: [
          { index: true, element: <TransactionsPage />, handle: { name: "Daftar" } },
          { path: "buat", element: <TransactionForm />, handle: { name: "Buat" } },
          { path: "edit/:id", element: <TransactionForm />, handle: { name: "Detail" } },
        ],
      },
      {
        path: "master",
        handle: { name: "Master" },
        children: [
          {
            path: "kode-akun",
            handle: { name: "Kode Akun" },
            children: [
              { index: true, element: <KodeAkunPage />, handle: { name: "Daftar" } },
              { path: "buat", element: <KodeAkunFormPage />, handle: { name: "Tambah" } },
              { path: "edit/:code", element: <KodeAkunFormPage />, handle: { name: "Edit" } }
            ]
          },
          {
            path: "pos-anggaran",
            handle: { name: "Pos Keuangan" },
            children: [
              { index: true, element: <PosAnggaranPage />, handle: { name: "Daftar" } },
              { path: "buat", element: <PosAnggaranFormPage />, handle: { name: "Tambah" } },
              { path: "edit/:id", element: <PosAnggaranFormPage />, handle: { name: "Edit" } }
            ]
          },
          {
            path: "periode",
            handle: { name: "Periode" },
            children: [
              { index: true, element: <PeriodePage />, handle: { name: "Daftar" } },
              { path: "buat", element: <PeriodeFormPage />, handle: { name: "Tambah" } },
              { path: "edit/:id", element: <PeriodeFormPage />, handle: { name: "Edit" } }
            ]
          },
          {
            path: "budget-settings",
            handle: { name: "Konfigurasi Anggaran" },
            children: [
              { index: true, element: <BudgetSettingsPage />, handle: { name: "Daftar" } },
              { path: "buat", element: <BudgetSettingsFormPage />, handle: { name: "Tambah" } },
              { path: "edit/:id", element: <BudgetSettingsFormPage />, handle: { name: "Edit" } }
            ]
          },
          {
            path: "fund-request-settings",
            element: <FundRequestSettingsPage />,
            handle: { name: "Konfigurasi Permohonan Dana" },
          },
          {
            path: "workflow",
            handle: { name: "Workflow" },
            children: [
              { index: true, element: <WorkflowPage />, handle: { name: "Daftar" } },
              { path: "buat", element: <WorkflowFormPage />, handle: { name: "Tambah" } },
              { path: "edit/:id", element: <WorkflowFormPage />, handle: { name: "Edit" } }
            ]
          }
        ]
      },
    ],
  },
  { path: "/login", element: <Login />, handle: { name: "Login" } },
  { path: "/auth", element: <Auth />, handle: { name:"Auth" }},
  { path: "*", element:<NotFound />, handle: { name: "NotFound"}},
]);

export default MainRouter;
